/* SpawnAnything — static voxel models for menu cards (units, AI recipes, buildings, weapons, tools): a toon body plus an
   inverted-hull outline, like the crate screen. Used by src/ui/preview3d.js; no animation, no instancing. */
import * as THREE from 'three';
import { MODELS, RECIPES, TYPES, VoxModel, WEAPONS, addOutline, meshAO, modelCenter, recipeModel } from '../game/voxel.js';
import { quadrupedModel } from '../game/templates/quadruped.js';
import { ERA_RECIPES, ERA_TYPES, ERA_WEAPONS } from '../content/eras.js';
import { compileBuilding, meshBuilding } from '../game/buildings.js';

const GRAD = (() => { const t = new THREE.DataTexture(new Uint8Array([90, 170, 255]), 3, 1, THREE.RedFormat); t.minFilter = t.magFilter = THREE.NearestFilter; t.needsUpdate = true; return t; })();
export const BODY = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: GRAD });
BODY.onBeforeCompile = sh => {
  sh.vertexShader = 'attribute float aEmit;\nvarying float vEmit;\n' + sh.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvEmit = aEmit;');
  sh.fragmentShader = 'varying float vEmit;\n' + sh.fragmentShader.replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += diffuseColor.rgb * vEmit * 1.5;');
};
BODY.customProgramCacheKey = () => 'menu-body';
export const LINE = new THREE.ShaderMaterial({
  side: THREE.BackSide,
  vertexShader: 'attribute vec3 aOut; void main(){ gl_Position = projectionMatrix * modelViewMatrix * vec4(position + aOut, 1.0); }',
  fragmentShader: 'void main(){ gl_FragColor = vec4(0.05, 0.05, 0.07, 1.0); }',
});

const ALL = () => ({ types: { ...TYPES, ...ERA_TYPES }, recipes: { ...RECIPES, ...ERA_RECIPES }, weapons: { ...WEAPONS, ...ERA_WEAPONS } });
const group = geo => { const g = new THREE.Group(); g.add(new THREE.Mesh(geo, BODY), new THREE.Mesh(geo, LINE)); return g; };
const ofModel = (m, team = 0) => group(addOutline(meshAO(m, team, modelCenter(m), 0.04, true, 1.15), m.vs * (m.detail || 1), 0.85));

/** a voxel recipe (hand-built or AI) as a model */
export function recipeObject(rec, team = 0, detail = 1) {
  const m = rec.template === 'quadruped' ? quadrupedModel(rec, { detail }) : recipeModel(rec, { t: 0.4, detail });
  return ofModel(m, team);
}
/** unit type key (TYPES / ERA_TYPES / types registered at runtime) incl. its weapon in hand (weaponKey overrides the default) */
export function unitObject(typeKey, team = 0, types, weaponKey, recipes, body = {}) {
  const A = ALL(), ty = (types && types[typeKey]) || A.types[typeKey];
  if (!ty) return new THREE.Group();
  const rec = ty.recipe ? ((recipes && recipes[ty.recipe]) || A.recipes[ty.recipe] || RECIPES[ty.recipe]) : null;
  const bo = { t: body.t ?? 0.4, width: body.width ?? 1, head: body.head ?? 1, detail: 1 }; // Körper / Statur / Kopf sliders
  const m = rec ? (rec.template === 'quadruped' ? quadrupedModel(rec, { detail: 1 }) : recipeModel(rec, bo)) : MODELS[typeKey]?.();
  if (!m) return new THREE.Group();
  const wp = A.weapons[weaponKey || ty.weapon] || WEAPONS[weaponKey || ty.weapon], mount = wp && (wp.melee ? m.mountMelee : m.mountRanged);
  if (wp && wp.vox && mount) {
    const prev = m.part; m.use(mount.part);
    wp.vox((f0, f1, s0, s1, u0, u1, c, glow) => {
      for (let f = f0; f <= f1; f++) for (let s = s0; s <= s1; s++) for (let u = u0; u <= u1; u++) {
        m.glow(!!glow); m.set(mount.a[0] + f * mount.f[0] + u * mount.u[0] + s * mount.s[0], mount.a[1] + f * mount.f[1] + u * mount.u[1] + s * mount.s[1], mount.a[2] + f * mount.f[2] + u * mount.u[2] + s * mount.s[2], typeof c === 'function' ? c(f, u, s) : c);
      }
      m.glow(0);
    });
    m.use(prev);
  }
  return ofModel(m, team);
}
/** building recipe at a level (1..3) */
export function buildingObject(recipe, team = 0, level = 1) {
  return group(meshBuilding(compileBuilding(recipe, level).model, team));
}
/** weapon key, lying diagonally */
export function weaponObject(key) {
  const w = ALL().weapons[key]; if (!w || !w.vox) return new THREE.Group();
  const m = new VoxModel(0.06);
  w.vox((f0, f1, s0, s1, u0, u1, c, glow) => {
    for (let f = f0; f <= f1; f++) for (let s = s0; s <= s1; s++) for (let u = u0; u <= u1; u++) { m.glow(!!glow); m.set(s, f, u, typeof c === 'function' ? c(f, u, s) : c); }
    m.glow(0);
  });
  const g = ofModel(m); g.rotation.z = -0.75; const w2 = new THREE.Group(); w2.add(g); return w2;
}
/** any VoxModel built by a caller (tools, items) */
export const voxObject = (m, team = 0) => ofModel(m, team);

/** grey 1.8 m person for size comparison (simple voxel blocks, origin at the feet) */
export function humanRef() {
  const m = new VoxModel(0.1), skin = 0xc9c3b8, cloth = 0x8d939c, dark = 0x5d636c;
  m.box(-2, 0, -1, -1, 7, 0, dark); m.box(0, 0, -1, 1, 7, 0, dark);      // legs 0.8 m
  m.box(-2, 8, -1, 1, 13, 0, cloth);                                   // torso
  m.box(-3, 8, -1, -3, 13, 0, cloth); m.box(2, 8, -1, 2, 13, 0, cloth);  // arms
  m.box(-1, 14, -1, 0, 17, 0, skin);                                   // head -> 1.8 m
  return ofModel(m, 0);
}
